import { IconButton, IconButtonProps } from '@mui/material';
import React, { type JSX, RefObject, useMemo } from 'react';

import { Tooltip } from '../../components/tooltip/Tooltip';
import { getLinkData } from '../base-action-button/utils';

import { BaseActionProps } from './types';

export type ActionTooltipProps = BaseActionProps & {
  icon: JSX.Element;
  tooltip: string;
  className?: string;
  rootRef?: RefObject<HTMLButtonElement>;
  hideTooltip?: boolean;
  disableReason?: string;
};

export function IconButtonWithTooltip<T extends React.ElementType>(
  props: IconButtonProps<T, ActionTooltipProps>
) {
  const {
    href,
    tooltip,
    className,
    icon,
    rootRef,
    dataTestId,
    disableReason,
    hideTooltip = false,
    getHref: _getHref,
    ...other
  } = props;
  const { linkProps } = getLinkData(href);

  const tooltipValue = useMemo(() => {
    if (props.disabled && disableReason) return disableReason;

    if (tooltip) return tooltip;
  }, [disableReason, props.disabled, tooltip]);

  const element = (
    <IconButton
      ref={rootRef}
      sx={{ height: '100%' }}
      color="primary"
      data-testid={dataTestId}
      {...other}
      {...linkProps}
    >
      {icon}
    </IconButton>
  );

  return (
    <Tooltip
      hide={hideTooltip}
      title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipValue}</span>}
      placement="top"
    >
      <span className={className}>{element}</span>
    </Tooltip>
  );
}
